import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Token } from './token.model';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  constructor(
    private tokenService: TokenService,
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document
  ) { }

  login(username: string, password: string): Observable<boolean> {
    return this.http.post<Token>('/api/token/', { username, password })
      .pipe(
        map(token => {
          this.tokenService.setToken(token);
          return true;
        })
      );
  }

  refresh(): Observable<boolean> {
    const token = this.tokenService.getToken();

    if (!token || this.isExpirated(token.refresh)) {
      return of(false);
    }
    const refresh = token.refresh;

    return this.http.post<Token>('/api/token/refresh/', { refresh })
      .pipe(
        switchMap(t => {
          this.tokenService.setToken(t);
          return of(true);
        }),
        catchError(error => {
          this.logout();
          return throwError(error);
        })
      );
  }

  getAuthorizationHeader(): string | string[] {
    return `Bearer ${this.tokenService.getToken().access}`;
  }

  isAuthenticated(): boolean {
    const token = this.tokenService.getToken();

    if (!token || this.isExpirated(token.access)) {
      return false;
    }
    return true;
  }

  redirectToLogin(): void {
    this.document.location.href = `/signin/?target=${this.document.location.href}`;
  }

  redirectToTarget(): void {
    const queryString = this.document.location.search;
    const urlParams = new URLSearchParams(queryString);
    const target = urlParams.get('target');
    if (target) {
      this.document.location.href = target;
    } else {
      this.document.location.href = '/';
    }
  }

  getAuthorizedUser(): Token {
    const token = this.tokenService.getToken();

    if (!token) {
      return null;
    }

    try {
      const { sub } = jwt_decode(token.access) as any;
      return JSON.parse(sub);
    } catch (error) {
      return null;
    }
  }

  logout(): void {
    this.tokenService.removeToken();
  }

  private isExpirated(hash: string): boolean {
    try {
      const jwt = jwt_decode(hash) as any;
      const expiration = jwt.exp * 1000;
      return expiration < Date.now();
    } catch (error) {
      this.logout();
      throw new Error('Invalid autentication token');
    }
  }

}
